import { apiError } from '../apiError';
import axiosInstance from '../axios.config';
import { ApiResponse } from 'types';

/**
 * What this portal calls itself, and which tabs it offers.
 *
 * Backend: core-service `GET /api/v1/portal-profile`. The profile belongs to a
 * SERVICE PROVIDER and is inherited by every business under it — Atmantan is a
 * business of CatchMyCall, so it receives CatchMyCall's portal.
 *
 * The server resolves the hierarchy and answers "what do I look like" in one
 * object. The client is deliberately not told who the parent is: a client that
 * can walk the org tree can also be made to render another reseller's brand.
 */
export interface PortalProfile {
  /** False for every organization until a reseller profile is written. */
  applies: boolean;
  providerOrganizationId?: string;

  /**
   * Identity overrides. Absent keys mean "keep whatever the hostname resolved",
   * which is what lets a reseller set only a name.
   */
  brandName?: string;
  brandOwner?: string;
  brandTagline?: string;
  logoUrl?: string;
  footerCompanyName?: string;
  showPoweredBy?: boolean;

  /**
   * Nav paths this portal may use. A path unlocks its children.
   *
   * EMPTY MEANS NO RESTRICTION — never "hide everything". A profile written
   * before the tab list was decided must leave the portal intact.
   */
  allowedPaths?: string[];

  /**
   * Which level decided the allowance — so "why are my tabs locked" has an
   * answer without reading two rows by hand.
   *
   * BRAND always comes from the provider; the allowance may be overridden per
   * business, because one reseller's customers can want very different portals.
   */
  allowedPathsSource?: 'PROVIDER' | 'BUSINESS';
}

export const portalProfileService = {
  /**
   * Upload a logo and store it on the profile in one call.
   *
   * Returns the whole profile rather than just a URL: uploading and saving as
   * two steps leaves an orphaned image and a profile that does not know about it
   * whenever the second one fails.
   */
  uploadLogo: async (
    organizationId: string,
    file: File,
  ): Promise<ApiResponse<PortalProfile>> => {
    try {
      const form = new FormData();
      form.append('file', file);
      // Content-Type is deliberately not set — the browser has to add the
      // multipart boundary, and naming the type by hand strips it.
      return (await axiosInstance.post(
        `/v1/portal-profile/${organizationId}/logo`, form,
      )).data;
    } catch (e: any) { return apiError(e, 'Failed to upload the logo'); }
  },

  mine: async (): Promise<ApiResponse<PortalProfile>> => {
    try { return (await axiosInstance.get('/v1/portal-profile')).data; }
    catch (e: any) { return apiError(e, 'Failed to load the portal profile'); }
  },

  forProvider: async (providerOrganizationId: string): Promise<ApiResponse<PortalProfile>> => {
    try { return (await axiosInstance.get(`/v1/portal-profile/${providerOrganizationId}`)).data; }
    catch (e: any) { return apiError(e, 'Failed to load that provider\'s portal profile'); }
  },

  save: async (
    providerOrganizationId: string,
    body: Partial<Omit<PortalProfile, 'applies' | 'providerOrganizationId'>>,
  ): Promise<ApiResponse<PortalProfile>> => {
    try { return (await axiosInstance.put(`/v1/portal-profile/${providerOrganizationId}`, body)).data; }
    catch (e: any) { return apiError(e, 'Failed to save the portal profile'); }
  },
};
